import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import { consumeLoginNotice } from '../lib/authRedirect';

export default function Login() {
  const {
    user,
    profile,
    signIn,
    initialAuthLoading,
    profileRefreshing,
    profileError,
    authMessage,
    clearAuthMessage,
  } = useAuth();
  const { config, loading: configLoading } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redirectMessage] = useState(() => consumeLoginNotice());

  const loginMessage = useMemo(() => {
    return authMessage || location.state?.authMessage || redirectMessage || null;
  }, [authMessage, location.state, redirectMessage]);

  useEffect(() => {
    if (user && profile?.status === 'active') {
      const target = location.state?.from?.pathname || '/dashboard';
      navigate(target, { replace: true });
    }
  }, [location.state, navigate, profile, user]);

  if (configLoading || initialAuthLoading) return null;

  const loginEnabled = config?.enable_login !== false;
  const allowSelfSignup = config?.enable_self_signup === true;
  const invitedOnly = !allowSelfSignup;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    clearAuthMessage();
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err.message || 'Unable to sign in.');
    } finally {
      setLoading(false);
    }
  };

  if (!loginEnabled) {
    return (
      <div className="min-h-screen bg-navy-950 hero-grid-bg flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-900/70 backdrop-blur-xl p-8 shadow-2xl text-center" data-testid="login-disabled">
          <div className="text-xs uppercase tracking-widest text-cyan-glow font-mono mb-3">Login disabled</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white mb-3">Workspace sign-in is currently unavailable</h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            Login has been turned off through the backend feature flag. Contact your administrator if you need access.
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-5 py-3 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-colors"
              data-testid="login-disabled-home"
            >
              Back home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 hero-grid-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8" data-testid="back-to-home">
          Back home
        </Link>

        <div className="rounded-2xl border border-white/10 bg-navy-900/70 backdrop-blur-xl p-8 shadow-2xl">
          <div className="text-xs uppercase tracking-widest text-cyan-glow font-mono mb-3">Sign in</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white mb-2">Welcome back</h1>
          <p className="text-sm text-slate-400 mb-6">
            {invitedOnly ? 'Invited users only - sign in to your organization.' : 'Continue your pre-shipment reviews.'}
          </p>

          {invitedOnly && (
            <div className="mb-6 p-3 rounded-lg bg-cyan-glow/5 border border-cyan-glow/20 text-cyan-glow text-xs flex items-start gap-2" data-testid="login-invite-only-banner">
              <span className="font-mono uppercase tracking-widest text-[10px] mt-0.5">Private pilot</span>
              <span className="text-cyan-glow/80">Public signup is disabled. Use the credentials sent to your email.</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          {loginMessage && (
            <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-200 text-sm" data-testid="login-auth-message">
              {loginMessage}
            </div>
          )}

          {profileError?.kind === 'service_unavailable' && (
            <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-sm" data-testid="login-service-unavailable">
              {profileError.message}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
            <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />

            <button
              type="submit"
              disabled={loading || profileRefreshing}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover transition-colors disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {(loading || profileRefreshing) ? 'Checking access...' : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="mt-6 text-sm text-slate-400 text-center">
            {invitedOnly ? (
              <>Need pilot access?{' '}
                <Link to="/request-access" className="text-cyan-glow hover:text-cyan-hover" data-testid="login-to-pilot">
                  Request access
                </Link>
              </>
            ) : allowSelfSignup ? (
              <>New here?{' '}
                <Link to="/signup" className="text-cyan-glow hover:text-cyan-hover" data-testid="login-to-signup">
                  Create an account
                </Link>
              </>
            ) : (
              <>Need workspace access?{' '}
                <Link to="/request-access" className="text-cyan-glow hover:text-cyan-hover" data-testid="login-to-pilot">
                  Request access
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="mt-2 w-full bg-navy-950 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-cyan-glow/50 focus:outline-none focus:ring-2 focus:ring-cyan-glow/20 transition-colors"
        data-testid={testid}
      />
    </label>
  );
}
